import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

interface SupportAccess {
  id: string; operatorEmail: string; tenantId: string; reason: string;
  startedAt: string; expiresAt: string; endedAt: string | null; active: boolean;
}

/// School-facing transparency: shows every time Eduvora platform support accessed this school,
/// who did it, why, and when. Support access is always read-only; this is how the school sees it.
export function SupportAccessPage({ embedded = false }: { embedded?: boolean }) {
  const [rows, setRows] = useState<SupportAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRows(await api.get<SupportAccess[]>('/api/tenant/support-access')); }
    catch (e) { setError(e instanceof ApiError && e.status === 403 ? 'Only school admins can view support access.' : 'Could not load support-access history.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Admin</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Support access</h1>
        </div>
      )}
      <p style={{ color: 'var(--ink-2)', marginTop: 0, marginBottom: 18, maxWidth: 640 }}>
        For transparency, every time Eduvora support accesses your school is recorded here. Support
        access is always <strong>read-only</strong> — support can view your data to help, never change it.
      </p>

      {error ? (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>{error}</div>
      ) : loading ? (
        <p style={{ color: 'var(--ink-3)' }}>Loading…</p>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
              <Th>When</Th><Th>Support operator</Th><Th>Reason</Th><Th>Status</Th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><span style={{ fontSize: 13 }}>{r.startedAt}</span></Td>
                  <Td><span className="mono" style={{ fontSize: 12 }}>{r.operatorEmail}</span></Td>
                  <Td><span style={{ fontSize: 13 }}>{r.reason}</span></Td>
                  <Td>
                    {r.active
                      ? <span className="pill pill-warn" style={{ fontSize: 11 }}>Active now</span>
                      : <span className="pill pill-muted" style={{ fontSize: 11 }}>Ended {r.endedAt ?? ''}</span>}
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && <tr><Td colSpan={4} muted>Eduvora support has never accessed your school.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: ReactNode }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em' }}>{children}</th>;
}
function Td({ children, colSpan, muted }: { children?: ReactNode; colSpan?: number; muted?: boolean }) {
  return <td colSpan={colSpan} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : 'left', fontSize: 13 }}>{children}</td>;
}
